import { OptionType } from '@/components/AutoComplete';
import toastMessage from '@/components/Toast';
import { UserProfilePagePermissions } from '@/models/Permissions/enum/UserProfilePagePermissions';
import type { ThunkApiConfig } from '@/models/ThunkApiConfig';
import { thunkError } from '@/utils/thunkError';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  CompanyEntityPayload,
  CompanyItem,
  CompanyPayload,
  CompanyRelationshipPayload,
  CompanyRelationshipUpdatePayload,
  CompanyTypeItem,
  GvMappingItem,
  GvSponsorWithMapping,
  HedgeStandardItem,
  OwnershipTypeItem,
} from './types';

export const fetchAllCompany = createAsyncThunk(
  'companyCenter/fetchAllCompany',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.companyEntity.getAllCompany();
      const resp = response.data;

      if (resp.success) {
        const parsed: CompanyItem[] = resp.data.map((item: any) => ({
          company_id: item.company_id,
          company_name: item.comp_name,
          credit_spread: item.comp_credit_spread,
          company_type_id: {
            label: item.comp_type_name,
            value: item.comp_type,
          },
          company_ownership_type_id: {
            label: item.comp_ownership_type_name,
            value: item.comp_ownership_type,
          },
          currency_id: {
            label: item.currency_code,
            value: item.currency_id,
          },
          gv_org_id: item.gv_org_id ?? null,
          account_summary: item.account_summary ?? null,
        }));

        return parsed;
      }

      return rejectWithValue('Error loading companies');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading companies',
      });
    }
  },
);

export const fetchAllCompanyTypes = createAsyncThunk(
  'companyCenter/fetchAllCompanyTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.companyEntity.getAllCompanyTypes();

      if (response.data.success) {
        const rawItems: CompanyTypeItem[] = response.data.data;

        const options: OptionType[] = rawItems.map((item) => ({
          label: item.description,
          value: item.comp_type_id,
        }));

        return options;
      }

      return rejectWithValue('Error loading company types');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading company types',
      });
    }
  },
);

export const fetchAllOwnershipTypes = createAsyncThunk(
  'companyCenter/fetchAllOwnershipTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.companyEntity.getAllOwnershipTypes();

      if (response.data.success) {
        const rawItems: OwnershipTypeItem[] = response.data.data;

        const options: OptionType[] = rawItems.map((item) => ({
          label: item.description,
          value: item.comp_ownership_type_id,
        }));

        return options;
      }

      return rejectWithValue('Error loading ownership types');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading ownership types',
      });
    }
  },
);

export const fetchAllGvMappings = createAsyncThunk(
  'companyCenter/fetchAllGvMappings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.companyEntity.getAllGvMappings();
      const resp = response.data;

      const rawItems: GvMappingItem[] = resp?.data ?? [];
      const options: OptionType[] = rawItems.map((item) => ({
        label: item.account_summary,
        value: item.gv_org_id,
      }));

      return options;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading Gold Vision mappings',
      });
    }
  },
);

export const fetchHedgeStandards = createAsyncThunk(
  'companyCenter/fetchHedgeStandards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.companyEntity.getHedgeStandards();

      if (response.data.success) {
        const rawItems: HedgeStandardItem[] = response.data.data;

        const options: OptionType[] = rawItems.map((item) => ({
          label: item.hedge_acc_stand_type,
          value: item.hedge_acc_stand_type_id,
        }));

        return options;
      }

      return rejectWithValue('Error loading hedge standards');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading hedge standards',
      });
    }
  },
);

export const createCompany = createAsyncThunk(
  'companyCenter/createCompany',
  async (data: CompanyPayload, { rejectWithValue }) => {
    try {
      toastMessage.info('Creating company...');

      const response = await apiInstance.companyEntity.createCompany(data);

      if (response.data?.success === false) {
        return rejectWithValue('Error creating company');
      }

      toastMessage.success('Company created successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error creating company',
      });
    }
  },
);

export const updateCompany = createAsyncThunk(
  'companyCenter/updateCompany',
  async (
    {
      company_id,
      data,
    }: {
      company_id: string;
      data: CompanyPayload;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      toastMessage.info('Updating company...');

      const response = await apiInstance.companyEntity.updateCompany(
        company_id,
        data,
      );

      if (response.data.success) {
        toastMessage.success('Company updated successfully');
        return response.data.data;
      }

      dispatch(fetchAllCompany());

      return rejectWithValue('Update failed');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error updating company',
      });
    }
  },
);

export const fetchSponsorList = createAsyncThunk(
  'companyCenter/fetchSponsorList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.filters.fetchSponsorList();
      const resp = response.data;

      if (resp.success) {
        return resp.data;
      }

      return rejectWithValue('Error loading sponsors');
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading sponsors',
      });
    }
  },
);

export const fetchAllCompanyEntities =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'companyCenter/fetchAllCompanyEntities',
    async (
      brieflyWithSponsors: boolean = false,
      { rejectWithValue, getState },
    ) => {
      const { permissions } = getState().auth;

      const permission = permissions.includes(
        UserProfilePagePermissions.manageCompanies,
      );

      if (!permission) return;

      try {
        const response =
          await apiInstance.companyEntity.getAllCompanyEntities(
            brieflyWithSponsors,
          );

        const resp = response.data;

        if (resp?.success === false) {
          return rejectWithValue('Error loading entities');
        }

        return resp.data;
      } catch (error) {
        return thunkError(error, rejectWithValue, {
          prefix: 'Error loading entities',
        });
      }
    },
  );

export const createCompanyEntity = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'companyCenter/createCompanyEntity',
  async (data: CompanyEntityPayload, { rejectWithValue, dispatch }) => {
    try {
      toastMessage.info('Creating company entity...');

      const response =
        await apiInstance.companyEntity.createCompanyEntity(data);

      if (response.data?.success === false) {
        return rejectWithValue('Error creating company entity');
      }

      // Refresh the grid the same way updateCompanyEntity does - without this the
      // new entity only appears after a manual refresh.
      dispatch(fetchAllCompanyEntities(false));

      toastMessage.success('Company entity created successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error creating company entity',
      });
    }
  },
);

export const updateCompanyEntity = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'companyCenter/updateCompanyEntity',
  async (
    { entity_id, data }: { entity_id: string; data: CompanyEntityPayload },
    { rejectWithValue, dispatch },
  ) => {
    try {
      toastMessage.info('Updating company entity...');

      const response = await apiInstance.companyEntity.updateCompanyEntity(
        entity_id,
        data,
      );

      const resp = response.data;

      dispatch(fetchAllCompanyEntities(false));

      if (resp?.success === false) {
        return rejectWithValue('Error updating entity');
      }

      toastMessage.success('Company entity updated successfully');
      return resp;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error updating entity',
      });
    }
  },
);

export const createCompanyRelationship = createAsyncThunk(
  'companyCenter/createCompanyRelationship',
  async (data: CompanyRelationshipPayload, { rejectWithValue, dispatch }) => {
    try {
      toastMessage.info('Creating company relationship...');

      const response =
        await apiInstance.companyEntity.createCompanyRelationship(data);

      if (response.data?.success === false) {
        return rejectWithValue('Error creating relationship');
      }

      dispatch(fetchSponsorList());

      toastMessage.success('Company relationship created successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error creating relationship',
      });
    }
  },
);

export const updateCompanyRelationship = createAsyncThunk(
  'companyCenter/updateCompanyRelationship',
  async (
    {
      relationship_id,
      data,
    }: {
      relationship_id: string;
      data: CompanyRelationshipUpdatePayload;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      toastMessage.info('Updating company relationship...');

      const response =
        await apiInstance.companyEntity.updateCompanyRelationship(
          relationship_id,
          data,
        );

      if (response.data?.success === false) {
        return rejectWithValue('Error updating relationship');
      }

      dispatch(fetchSponsorList());

      toastMessage.success('Company relationship updated successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error updating relationship',
      });
    }
  },
);

export const fetchGvSponsorList = createAsyncThunk(
  'companyCenter/fetchGvSponsorList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.filters.fetchGvSponsorList();
      const data = (response.data?.data ?? response.data) as
        | GvSponsorWithMapping[]
        | undefined;

      if (!Array.isArray(data)) {
        return rejectWithValue('Error loading GV sponsors');
      }

      return data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error loading GV sponsors',
      });
    }
  },
);

export const linkGvSponsorMapping = createAsyncThunk(
  'companyCenter/linkGvSponsorMapping',
  async (
    { gvSponsorId, companyId }: { gvSponsorId: string; companyId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      toastMessage.info('Linking GV sponsor...');

      const response = await apiInstance.filters.linkGvSponsorMapping(
        gvSponsorId,
        companyId,
      );

      if (response.data?.success === false) {
        return rejectWithValue('Error linking GV sponsor');
      }

      dispatch(fetchSponsorList());

      toastMessage.success('GV sponsor linked successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error linking GV sponsor',
      });
    }
  },
);

export const unlinkGvSponsorMapping = createAsyncThunk(
  'companyCenter/unlinkGvSponsorMapping',
  async (gvSponsorId: string, { rejectWithValue, dispatch }) => {
    try {
      toastMessage.info('Unlinking GV sponsor...');

      const response =
        await apiInstance.filters.unlinkGvSponsorMapping(gvSponsorId);

      if (response.data?.success === false) {
        return rejectWithValue('Error unlinking GV sponsor');
      }

      dispatch(fetchSponsorList());

      toastMessage.success('GV sponsor unlinked successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error unlinking GV sponsor',
      });
    }
  },
);

export const deleteCompanyRelationship = createAsyncThunk(
  'companyCenter/deleteCompanyRelationship',
  async (relationship_id: string, { rejectWithValue, dispatch }) => {
    try {
      toastMessage.info('Deleting company relationship...');

      const response =
        await apiInstance.companyEntity.deleteCompanyRelationship(
          relationship_id,
        );

      if (response.data?.success === false) {
        return rejectWithValue('Error deleting relationship');
      }

      dispatch(fetchSponsorList());

      toastMessage.success('Company relationship deleted successfully');
      return response.data;
    } catch (error) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Error deleting relationship',
      });
    }
  },
);
